import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { EventName, GalleryType } from '@/vdp/Gallery/Carousel/constants';
import GalleryImageType from '~/constants/vdp/galleryImageType';

const defaultContext = {
  closeFullScreen: () => {},
  imageType: '' as GalleryImageType,
  isFullScreen: false,
  openFullScreen: (_imgId: string, _imgType: GalleryImageType) => {},
  openImperfections: (_index?: string) => {},
  openFeatures: (_index?: string) => {},
  setSlideImgId: (_imgId: string) => {},
  slideImgId: ''
};

type ContextType = typeof defaultContext;

const CarouselContext = createContext<ContextType>(defaultContext);

export function CarouselContextProvider({ children }: React.PropsWithChildren) {
  const { trackEvent } = useContext(AnalyticsContext);

  const [isFullScreen, setFullScreen] = useState(false);
  const [slideImgId, setSlideImgId] = useState('');
  const [imageType, setImageType] = useState<GalleryImageType>(
    GalleryImageType.All
  );

  const openFullScreen = useCallback(
    (imgId: string, imgType: GalleryImageType) => {
      setSlideImgId(imgId);
      setFullScreen(true);
      trackEvent('VDP - Gallery - Clicked into full-screen mode');
      trackEvent(EventName.galleryEngaged, {
        galleryType: GalleryType.FullScreen,
        imageType: imgType
      });
    },
    [trackEvent]
  );

  const closeFullScreen = useCallback(() => {
    setFullScreen(false);
    setImageType(GalleryImageType.All);
    if (imageType === GalleryImageType.Imperfection) {
      setSlideImgId('');
    }
  }, [imageType]);

  const openFeatures = useCallback(
    (id: string = '') => {
      openFullScreen(id, GalleryImageType.Feature);
      setImageType(GalleryImageType.Feature);
    },
    [openFullScreen]
  );

  const openImperfections = useCallback(
    (id: string = '') => {
      openFullScreen(id, GalleryImageType.Imperfection);
      setImageType(GalleryImageType.Imperfection);
    },
    [openFullScreen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      closeFullScreen,
      imageType,
      isFullScreen,
      openFeatures,
      openFullScreen,
      openImperfections,
      setSlideImgId,
      slideImgId
    }),
    [
      closeFullScreen,
      imageType,
      isFullScreen,
      openFeatures,
      openFullScreen,
      openImperfections,
      slideImgId
    ]
  );

  return (
    <CarouselContext.Provider value={value}>
      {children}
    </CarouselContext.Provider>
  );
}

CarouselContextProvider.displayName = 'CarouselContextProvider';

export default CarouselContext;
